import React, { useState, useEffect } from 'react';
import { Input, FormGroup } from '../components/Form';
import { Plus, X, Edit2, Trash2, ToggleLeft, ToggleRight } from 'lucide-react';
import { getCargos, salvarCargo, toggleCargo } from '../services/cargos';

const EMPTY_FORM = {
    id: null,
    nome: '',
    slug_canonical: '',
    nivel: 1,
    comissao_agendamento_tipo: 'fixed',
    comissao_agendamento_valor: '',
    comissao_venda_tipo: 'percent',
    comissao_venda_valor: '',
    comissao_venda_override_valor: '',
    comissao_indicacao_tipo: 'percent',
    comissao_indicacao_valor: '',
    comissao_contato_tipo: 'fixed',
    comissao_contato_valor: '',
    salario_base: '',
    meta_mensal: '',
    descricao: '',
    ativo: true,
};

function agruparPorNome(cargos) {
    const map = {};
    for (const c of cargos) {
        if (!map[c.nome]) map[c.nome] = [];
        map[c.nome].push(c);
    }
    return Object.entries(map);
}

export default function Cargos() {
    const [cargos, setCargos] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('add'); // 'add' | 'edit' | 'nivel'
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [statusMessage, setStatusMessage] = useState({ type: '', text: '' });
    const [formData, setFormData] = useState(EMPTY_FORM);

    const carregar = async () => {
        setIsLoading(true);
        try {
            setCargos(await getCargos());
        } catch {
            setStatusMessage({ type: 'error', text: 'Erro ao carregar cargos.' });
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => { carregar(); }, []);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => {
            const next = { ...prev, [name]: value };
            if (name === 'nome' && modalMode === 'add') {
                next.slug_canonical = value.trim().toLowerCase().replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
            }
            return next;
        });
    };

    const openModal = (mode, cargo = null) => {
        setModalMode(mode);
        setStatusMessage({ type: '', text: '' });
        if (mode === 'edit' && cargo) {
            setFormData({
                id: cargo.id,
                nome: cargo.nome,
                slug_canonical: cargo.slug_canonical,
                nivel: cargo.nivel ?? 1,
                comissao_agendamento_tipo: cargo.comissao_agendamento_tipo,
                comissao_agendamento_valor: cargo.comissao_agendamento_valor,
                comissao_venda_tipo: cargo.comissao_venda_tipo,
                comissao_venda_valor: cargo.comissao_venda_valor,
                comissao_venda_override_valor: cargo.comissao_venda_override_valor ?? '',
                comissao_indicacao_tipo: cargo.comissao_indicacao_tipo,
                comissao_indicacao_valor: cargo.comissao_indicacao_valor,
                comissao_contato_tipo: cargo.comissao_contato_tipo ?? 'fixed',
                comissao_contato_valor: cargo.comissao_contato_valor ?? '',
                salario_base: cargo.salario_base ?? '',
                meta_mensal: cargo.meta_mensal ?? '',
                descricao: cargo.descricao ?? '',
                ativo: cargo.ativo,
            });
        } else {
            setFormData(EMPTY_FORM);
        }
        setIsModalOpen(true);
    };

    const openNivel = (nome, slug_canonical, proximoNivel) => {
        setModalMode('nivel');
        setStatusMessage({ type: '', text: '' });
        setFormData({
            ...EMPTY_FORM,
            nome,
            slug_canonical,
            nivel: proximoNivel,
        });
        setIsModalOpen(true);
    };

    const closeModal = () => { setIsModalOpen(false); setFormData(EMPTY_FORM); };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setStatusMessage({ type: '', text: '' });
        try {
            const acao = modalMode === 'edit' ? 'editar' : 'adicionar';
            await salvarCargo({ ...formData, acao });
            setStatusMessage({ type: 'success', text: `Cargo ${modalMode === 'edit' ? 'atualizado' : 'criado'} com sucesso!` });
            carregar();
            setTimeout(() => closeModal(), 1200);
        } catch (err) {
            setStatusMessage({ type: 'error', text: err?.message || 'Erro ao salvar cargo.' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = async (cargo) => {
        if (!window.confirm(`Remover "${cargo.nome} — Nível ${cargo.nivel}"? Pessoas vinculadas perderão o vínculo.`)) return;
        try {
            await salvarCargo({ acao: 'excluir', id: cargo.id });
            carregar();
        } catch (err) {
            setStatusMessage({ type: 'error', text: err?.message || 'Erro ao remover cargo.' });
        }
    };

    const handleToggle = async (cargo) => {
        try {
            await toggleCargo(cargo.id, !cargo.ativo);
            carregar();
        } catch (err) {
            setStatusMessage({ type: 'error', text: err?.message || 'Erro ao alternar ativo.' });
        }
    };

    const grupos = agruparPorNome(cargos);

    const modalTitle = modalMode === 'edit'
        ? 'Editar Cargo'
        : modalMode === 'nivel'
        ? `Novo Nível — ${formData.nome}`
        : 'Novo Cargo';

    const submitLabel = modalMode === 'edit' ? 'Salvar Alterações' : 'Criar Cargo';

    return (
        <>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '1rem 1.75rem 0' }}>
                <button className="pill pill-primary pill-sm" onClick={() => openModal('add')}>
                    <Plus size={14} style={{ marginRight: '0.3rem' }} /> Novo Cargo
                </button>
            </div>

            <div className="page-body" style={{ minHeight: 'auto' }}>
                {statusMessage.text && !isModalOpen && (
                    <div className={`alert alert-${statusMessage.type === 'success' ? 'success' : 'error'}`}>
                        {statusMessage.text}
                    </div>
                )}

                {isLoading ? (
                    <div className="loading-state">Carregando cargos...</div>
                ) : cargos.length === 0 ? (
                    <div className="empty-state">Nenhum cargo cadastrado.</div>
                ) : (
                    <div className="card">
                        <p className="section-label">
                            Cargos — {grupos.length} função(ões), {cargos.length} nível(is)
                        </p>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            {grupos.map(([nome, niveis], gi) => (
                                <div
                                    key={nome}
                                    style={{
                                        borderBottom: gi < grupos.length - 1 ? '1px solid var(--glass-border)' : 'none',
                                        paddingBottom: '0.5rem',
                                        marginBottom: gi < grupos.length - 1 ? '0.5rem' : 0,
                                    }}
                                >
                                    {/* Cabeçalho do grupo */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        paddingTop: gi > 0 ? '0.5rem' : 0,
                                        paddingBottom: '0.25rem',
                                    }}>
                                        <p style={{ margin: 0, fontWeight: 700, fontSize: '0.9375rem', color: 'var(--ink)' }}>
                                            {nome}
                                        </p>
                                        <button
                                            className="pill pill-ghost pill-sm"
                                            onClick={() => openNivel(
                                                nome,
                                                niveis[0].slug_canonical,
                                                Math.max(...niveis.map(n => n.nivel)) + 1,
                                            )}
                                            style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                        >
                                            <Plus size={13} /> Novo Nível
                                        </button>
                                    </div>

                                    {/* Níveis do grupo */}
                                    {niveis.map((cargo) => (
                                        <div
                                            key={cargo.id}
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'space-between',
                                                padding: '0.5rem 0 0.5rem 1rem',
                                                opacity: cargo.ativo ? 1 : 0.5,
                                            }}
                                        >
                                            <div>
                                                <p style={{ margin: 0, fontWeight: 500, fontSize: '0.875rem', color: 'var(--ink)' }}>
                                                    Nível {cargo.nivel}
                                                </p>
                                                <p style={{ margin: '0.15rem 0 0', fontSize: '0.75rem', color: 'var(--muted)' }}>
                                                    {cargo.salario_base ? `Base: R$ ${parseFloat(cargo.salario_base).toFixed(2)} · ` : ''}
                                                    Comissões gerenciadas na área Comissionamento
                                                </p>
                                            </div>
                                            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                                                <button
                                                    className="pill pill-ghost pill-sm"
                                                    onClick={() => handleToggle(cargo)}
                                                    title={cargo.ativo ? 'Desativar' : 'Ativar'}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.25rem' }}
                                                >
                                                    {cargo.ativo
                                                        ? <ToggleRight size={14} style={{ color: 'var(--emerald)' }} />
                                                        : <ToggleLeft size={14} />
                                                    }
                                                </button>
                                                <button
                                                    className="pill pill-ghost pill-sm"
                                                    onClick={() => openModal('edit', cargo)}
                                                    style={{ display: 'flex', alignItems: 'center', gap: '0.3rem' }}
                                                >
                                                    <Edit2 size={13} /> Editar
                                                </button>
                                                <button
                                                    className="pill pill-ghost pill-sm"
                                                    onClick={() => handleDelete(cargo)}
                                                    style={{ color: 'var(--danger-soft)', borderColor: 'rgba(239, 68, 68, 0.3)' }}
                                                >
                                                    <Trash2 size={13} />
                                                </button>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {isModalOpen && (
                <div className="modal-overlay">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h2>{modalTitle}</h2>
                            <button className="modal-close" onClick={closeModal}><X size={22} /></button>
                        </div>
                        <form onSubmit={handleSubmit} className="modal-body">
                            {statusMessage.text && (
                                <div className={`alert alert-${statusMessage.type === 'success' ? 'success' : 'error'}`} style={{ marginBottom: '1rem' }}>
                                    {statusMessage.text}
                                </div>
                            )}

                            <div className="grid-2">
                                <FormGroup label="Nome do Cargo" htmlFor="nome">
                                    <Input
                                        id="nome"
                                        name="nome"
                                        value={formData.nome}
                                        onChange={handleChange}
                                        required
                                        placeholder="Ex: Closer"
                                        readOnly={modalMode === 'nivel'}
                                    />
                                </FormGroup>
                                <FormGroup label="Nível" htmlFor="nivel">
                                    <Input
                                        id="nivel"
                                        name="nivel"
                                        type="number"
                                        min="1"
                                        value={formData.nivel}
                                        onChange={handleChange}
                                        required
                                        readOnly={modalMode === 'nivel'}
                                    />
                                </FormGroup>
                            </div>

                            <div style={{ margin: '1rem 0', padding: '0.75rem', border: '1px solid var(--emerald-200)', borderRadius: '0.5rem', background: 'var(--emerald-100)', color: 'var(--muted)', fontSize: '0.75rem', lineHeight: 1.5 }}>
                                Taxas, percentuais e bônus deste cargo agora são configurados em <a href="/comissionamento" style={{ color: 'var(--emerald)', fontWeight: 600 }}>Comissionamento</a>. Editar o cargo aqui preserva os valores já cadastrados.
                            </div>

                            <p style={{ margin: '1rem 0 0.5rem', fontSize: '0.8rem', fontWeight: 600, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                                Dados do Nível (Opcional)
                            </p>
                            <div className="grid-2">
                                <FormGroup label="Salário Base (R$)" htmlFor="salario_base">
                                    <Input id="salario_base" name="salario_base" type="number" step="0.01" value={formData.salario_base} onChange={handleChange} placeholder="Ex: 2500.00" />
                                </FormGroup>
                                <FormGroup label="Meta Mensal (R$)" htmlFor="meta_mensal">
                                    <Input id="meta_mensal" name="meta_mensal" type="number" step="0.01" value={formData.meta_mensal} onChange={handleChange} placeholder="Ex: 50000.00" />
                                </FormGroup>
                            </div>
                            <FormGroup label="Descrição" htmlFor="descricao">
                                <textarea
                                    id="descricao"
                                    name="descricao"
                                    value={formData.descricao}
                                    onChange={handleChange}
                                    placeholder="Requisitos e responsabilidades deste nível..."
                                    rows={3}
                                    style={{ width: '100%', resize: 'vertical', padding: '0.5rem 0.75rem', borderRadius: '8px', border: '1px solid var(--glass-border)', background: 'var(--glass)', color: 'var(--ink)', fontSize: '0.875rem', fontFamily: 'inherit', boxSizing: 'border-box' }}
                                />
                            </FormGroup>

                            <div className="modal-footer">
                                <button type="button" className="pill pill-ghost" onClick={closeModal}>Cancelar</button>
                                <button type="submit" className="pill pill-primary" disabled={isSubmitting}>
                                    {isSubmitting ? 'Salvando...' : submitLabel}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </>
    );
}
